"use client";

import { default as NextImage } from "next/image";
import { useSearchParams } from "next/navigation";
import { useState, Suspense, useEffect } from "react";

// Stelle sicher, dass die Pfade zu deinen Bildern stimmen
import TeamViewver from "./TeamViewver.webp";
import RustDesk from "./RustDesk.webp";
import anydesk from "@/app/support/anydesk.png";

function KontaktForm() {
  const searchParams = useSearchParams();

  // URL Parameter auslesen
  const produkt = searchParams.get("produkt") ?? "";
  const preis = searchParams.get("preis") ?? "";
  const subject = searchParams.get("subject") ?? "";
  const body = searchParams.get("body") ?? "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [betreff, setBetreff] = useState("");
  const [nachricht, setNachricht] = useState("");
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  // Innerhalb von KontaktForm
  useEffect(() => {
    // Betreff setzen
    if (subject) {
      setBetreff(decodeURIComponent(subject));
    } else if (produkt) {
      setBetreff(`Anfrage: ${decodeURIComponent(produkt)}`);
    }

    // Nachricht setzen
    if (body) {
      setNachricht(decodeURIComponent(body));
    } else if (produkt) {
      const decProd = decodeURIComponent(produkt);
      const decPreis = decodeURIComponent(preis);
      setNachricht(
        `Ich interessiere mich für: ${decProd}${decPreis ? ` (${decPreis})` : ""}\n\nBitte nehmen Sie Kontakt mit mir auf.`,
      );
    }
  }, [subject, body, produkt, preis]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");

    const payload = {
      name,
      email,
      product: betreff || "Support-Anfrage",
      nachricht,
      unternehmen: "Support-Seite Anfragender",
    };

    try {
      const response = await fetch("/api/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus("success");
        setName("");
        setEmail("");
        setNachricht("");
        setBetreff("");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Versandfehler:", error);
      setStatus("error");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <form className="grid grid-cols-2 gap-4" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-black focus:outline-none focus:ring-2 focus:ring-blue-900"
          required
        />
        <input
          type="email"
          placeholder="E-Mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-black focus:outline-none focus:ring-2 focus:ring-blue-900"
          required
        />
        <input
          type="text"
          placeholder="Betreff"
          value={betreff}
          onChange={(e) => setBetreff(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-black focus:outline-none col-span-2 focus:ring-2 focus:ring-blue-900"
        />
        <textarea
          placeholder="Nachricht"
          rows={3}
          value={nachricht}
          onChange={(e) => setNachricht(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-black focus:outline-none col-span-2 focus:ring-2 focus:ring-blue-900"
          required
        />
        <button
          type="submit"
          disabled={status === "sending"}
          className="inline-flex items-center justify-center rounded-2xl px-5 py-3 text-base font-semibold bg-blue-900 hover:bg-blue-950 text-white shadow transition-all disabled:bg-slate-400"
        >
          {status === "sending" ? "Wird gesendet..." : "Nachricht senden"}
        </button>
      </form>

      {status === "success" && (
        <p className="text-green-600 font-semibold bg-green-50 p-3 rounded-lg border border-green-200">
          ✅ Nachricht erfolgreich gesendet! Wir melden uns bei Ihnen.
        </p>
      )}
      {status === "error" && (
        <p className="text-red-600 font-semibold bg-red-50 p-3 rounded-lg border border-red-200">
          ❌ Fehler beim Senden. Bitte versuchen Sie es später erneut.
        </p>
      )}
    </div>
  );
}

export default function Support() {
  return (
    <main className="min-h-screen bg-white">
      <section className="flex flex-col gap-4 items-center w-full px-4 py-20 overflow-hidden">
        <div className="flex flex-col text-center gap-4 w-full xl:w-2/3">
          <h1 className="font-bold text-4xl/tight sm:text-5xl/tight text-slate-900">
            Kontakt & Support
          </h1>
          <div className="grid gap-6 md:grid-cols-2 mt-8">
            <div className="flex flex-col justify-center text-center md:text-right">
              <p className="text-3xl font-semibold text-slate-800">
                Montag - Freitag
              </p>
              <p className="text-2xl text-slate-600">10:00 - 13:00</p>
              <p className="text-2xl text-slate-600">14:00 - 18:00</p>
            </div>
            <div
              id="formular"
              className="bg-slate-50 p-6 rounded-3xl shadow-sm border border-slate-100"
            >
              <Suspense
                fallback={<div className="animate-pulse">Lädt Formular...</div>}
              >
                <KontaktForm />
              </Suspense>
            </div>
          </div>
        </div>
      </section>

      <section
        id="spacer"
        className="flex flex-col gap-4 items-center w-full px-4 py-10 overflow-hidden bg-black text-white text-center"
      >
        <div className="max-w-2xl">
          <p className="text-xl leading-relaxed">
            Sie behalten die Kontrolle. Unsere Fernwartung lässt uns nur auf
            Ihren Rechner zugreifen, wenn Sie das explizit erlauben.
          </p>
        </div>
      </section>

      <h2 className="font-bold text-3xl sm:text-4xl text-center text-slate-900 mt-10">
        Sicherer Remote Access
      </h2>

      <section className="flex flex-col gap-12 items-center w-full px-4 py-20 overflow-hidden max-w-6xl mx-auto">
        {/* TeamViewer */}
        <div className="grid gap-10 md:grid-cols-2 items-center w-full">
          <div className="order-2 md:order-1">
            <NextImage
              src={TeamViewver}
              alt="TeamViewer"
              width={250}
              height={100}
              className="mx-auto md:ml-auto rounded-lg shadow-lg"
            />
          </div>
          <div className="order-1 md:order-2 text-center md:text-left">
            <a
              className="inline-flex items-center justify-center rounded-2xl px-6 py-3 text-base font-semibold bg-blue-900 hover:bg-blue-950 text-white shadow-lg transition-all"
              href="https://s3.sdigroup.de/sdigroup/GSDI-Fernwartung.exe"
              target="_blank"
              rel="noreferrer"
            >
              TeamViewer herunterladen
            </a>
            <p className="py-4 text-lg text-slate-600">
              Etablierte Lösung für schnellen Support und Online-Meetings. Ideal
              für externe Wartung und Dateiübertragungen.
            </p>
          </div>
        </div>

        {/* RustDesk */}
        <div className="grid gap-10 md:grid-cols-2 items-center w-full">
          <div>
            <NextImage
              src={RustDesk}
              alt="RustDesk"
              width={400}
              height={200}
              className="mx-auto md:ml-auto rounded-lg shadow-lg"
            />
          </div>
          <div className="text-center md:text-left">
            <a
              className="inline-flex items-center justify-center rounded-2xl px-6 py-3 text-base font-semibold bg-blue-900 hover:bg-blue-950 text-white shadow-lg transition-all"
              href="https://s3.sdigroup.de/sdigroup/GSDI-Fernwartung-x86_64.exe"
              target="_blank"
              rel="noreferrer"
            >
              RustDesk herunterladen
            </a>
            <p className="py-4 text-lg text-slate-600">
              Open-Source-Alternative mit Fokus auf Datenschutz und Kontrolle
              über die Infrastruktur.
            </p>
          </div>
        </div>

        {/* AnyDesk */}
        <div className="grid gap-10 md:grid-cols-2 items-center w-full">
          <div>
            <NextImage
              src={anydesk}
              alt="AnyDesk"
              width={400}
              height={300}
              className="mx-auto md:ml-auto rounded-lg shadow-lg"
            />
          </div>
          <div className="text-center md:text-left">
            <a
              className="inline-flex items-center justify-center rounded-2xl px-6 py-3 text-base font-semibold bg-blue-900 hover:bg-blue-950 text-white shadow-lg transition-all"
              href="https://anydesk.com/de/downloads/thank-you?dv=win_exe"
              target="_blank"
              rel="noreferrer"
            >
              AnyDesk herunterladen
            </a>
            <p className="py-4 text-lg text-slate-600">
              Schnelle und sichere Remote-Support-Lösung, ideal für die Wartung
              externer Systeme.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
